// dataCache.js — session-level read cache (2026-07-10, after a Firestore
// free-tier "Quota exceeded" day).
//
// THE PROBLEM: almost every page re-downloaded the full products collection
// (~800 docs) on every visit — catalog, orders, promotions, barcode mapping,
// packing sheets — burning ~800 reads per page view. One busy testing day
// exhausted the free tier's daily read quota and locked the whole app.
//
// THE FIX: fetch once, share everywhere, refresh after a short TTL or when
// something writes. Pages using the cache read 0 documents on navigation.
//
// Freshness notes:
// - Stock/promo counts shown in the catalog can be up to TTL stale. That's
//   display only — the ORDER TRANSACTION always prices and deducts from live
//   Firestore data, so staleness can never corrupt an order.
// - Any code path that WRITES products/customers must call the matching
//   invalidate*() so the next read refetches.

import { db } from "../config/firebase";
import { collection, getDocs } from "firebase/firestore";

const TTL_MS = 5 * 60 * 1000;

function makeCache(collectionName) {
  let entry = null;    // { at, docs }
  let inflight = null; // dedupe concurrent fetches

  return {
    async get(force = false) {
      if (!force && entry && Date.now() - entry.at < TTL_MS) return entry.docs;
      if (!inflight) {
        inflight = getDocs(collection(db, collectionName))
          .then(snap => {
            entry = { at: Date.now(), docs: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
            return entry.docs;
          })
          .finally(() => { inflight = null; });
      }
      return inflight;
    },
    invalidate() { entry = null; }
  };
}

const productsCache = makeCache("products");
const customersCache = makeCache("customers");
const qbSalesCache = makeCache("qbSales");

/** All products (active + inactive), cached ~5 min per tab. Docs include id. */
export const getAllProducts = (force) => productsCache.get(force);
export const invalidateProductCache = () => productsCache.invalidate();

/** All customer RECORDS, cached ~5 min per tab. Docs include id. */
export const getCustomerRecords = (force) => customersCache.get(force);
export const invalidateCustomerCache = () => customersCache.invalidate();

/** QB sales month docs (Analytics v2), ~1 doc per month. Docs include id. */
export const getQbSales = (force) => qbSalesCache.get(force);
export const invalidateQbSalesCache = () => qbSalesCache.invalidate();
